/*
 * Copyright (c) 2019 - Indigen Solutions
 * Authors:
 *   - Bichoy Messiha <bm@indigen.com>
 * NOTICE: All information contained herein is, and remains
 * the property of Indigen Solutions and its suppliers,
 * if any.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Indigen Solutions.
 */

import { concat, filter, map, slice, sortBy, uniqBy, isEmpty } from "lodash";
import MiniSearch from "minisearch";
import { BaseSearch } from "./BaseSearch";
import { Services } from "./Services";
import { isTodayAfter2025 } from "../helpers/naf.helper";

export class SearchService extends BaseSearch {
    constructor() {
        super();
        this.apenafs = [];
        this.apenafsEngine = null;
        this.numberOfCharToSearch = 2;
    }

    async init() {
        const { conventions, updatedAt } = await Services.api.convention.getSearchable();
        this.conventions = conventions;
        this.apenafs = isTodayAfter2025()
            ? await Services.api.ca.getAllApenafs()
            : await Services.api.ca.getApenafsNot2025();
        const isUpToDate = this.checkLocalStorageEnginesUpdated(updatedAt);
        const searchOptions = {
            prefix: true,
            combineWith: "AND",
        };

        this.initEngine(
            "conventionsEngine",
            {
                fields: [
                    "title",
                    "shortTitle",
                    "description",
                    "activitiesBody",
                    "activitiesTitles",
                    "brochure",
                    "activitiesCode",
                    "idcc",
                    "isDenounced",
                    "type",
                ],
                storeFields: [
                    "conventionId",
                    "variantNumber",
                    "parentConventionId",
                    "brochure",
                    "title",
                    "shortTitle",
                    "idcc",
                    "isDenounced",
                    "type",
                ],
                processTerm: term =>
                    term.length < this.numberOfCharToSearch ? null : this.clean(term),
                searchOptions,
            },
            this.conventions,
            isUpToDate
        );

        this.initEngine(
            "apenafsEngine",
            {
                fields: ["code", "label", "type"],
                storeFields: ["code", "label", "type"],
                searchOptions,
            },
            Object.values(this.apenafs),
            isUpToDate
        );
    }

    /**
     * Suggestions
     * @param query
     */
    getSuggestions(query) {
        if (!query) return [];
        query = this.clean(query);

        let suggestions = [];
        if (query.match(/\d{1,5}.{1,3}/)) {
            const apenafsSuggestions = slice(this.getSuggestionsApenafs(query), 0, 3);
            const brochureSuggestions = slice(this.getSuggestionsBrochures(query), 0, 3);
            const idccSuggestions = slice(this.getSuggestionsIdcc(query), 0, 3);
            suggestions = sortBy(
                concat(apenafsSuggestions, brochureSuggestions, idccSuggestions),
                s => s.score
            );
        }
        // else {
        //    suggestions = slice(this.getSuggestionsConventions(query), 0, 5);
        //}
        return suggestions;
    }

    /**
     * Suggestions
     * @param query
     */
    getPanoramaSuggestions(query) {
        if (!query) return [];
        query = this.clean(query);

        const brochureSuggestions = this.getSuggestionsBrochuresWithShortTitlePanorama(query);
        const idccSuggestions = this.getSuggestionsIdccWithShortTitlePanorama(query);
        return uniqBy(
            sortBy(concat(idccSuggestions, brochureSuggestions), s => s.label.toLowerCase()).filter(
                s => !isEmpty(s.suggestion)
            ),
            "label"
        );
    }

    /**
     * Search results by ActivityCodes
     * @param query
     */
    searchConventionsActivitiesCodes(query, territory) {
        if (!this.conventionsEngine) return [];
        let results = this.conventionsEngine.search(query.toLowerCase(), {
            fields: ["activitiesCode"],
            fuzzy: 0,
            prefix: false,
        });
        return this.manageResults(results, territory, 0);
    }

    /* . */

    /**
     *
     * @param query
     */
    getSuggestionsConventions(query) {
        if (!this.conventionsEngine) return;
        let results = this.conventionsEngine.autoSuggest(query, {
            fuzzy: 0.3,
            fields: ["title", "description"],
        });
        results = filter(results, r => {
            return !r.suggestion.match(/\d/) && r.suggestion.length > 3 && r.terms.length <= 3;
        });
        return map(results, r => {
            return {
                suggestion: r.suggestion,
                type: "text",
                score: r.score,
            };
        });
    }

    /**
     *
     * @param query
     */
    getSuggestionsApenafs(query) {
        if (!this.apenafsEngine) return;
        let results = this.apenafsEngine.search(query, { boost: { code: 2 } });
        return map(results, r => {
            return {
                suggestion: r.code,
                type: "code",
                subtype: r.type,
                label: r.label,
                score: r.score,
            };
        });
    }

    /**
     *
     * @param name
     * @param options
     * @param documents
     */
    initEngine(name, options, documents, isUpToDate) {
        const engine = localStorage.getItem(name);
        if (engine && isUpToDate) {
            this[name] = MiniSearch.loadJSON(engine, options);
        } else {
            this[name] = new MiniSearch(options);
            this[name].addAll(documents);
            localStorage.setItem(name, JSON.stringify(this[name].toJSON()));
        }
    }

    checkLocalStorageEnginesUpdated(updatedAt) {
        const storedUpdatedAt = localStorage.getItem("conventionSearchablesUpdatedAt");
        const isUpToDate =
            storedUpdatedAt &&
            updatedAt &&
            Services.moment(storedUpdatedAt).isSameOrAfter(updatedAt);

        if (!isUpToDate) {
            localStorage.setItem("conventionSearchablesUpdatedAt", updatedAt);
        }
        return isUpToDate;
    }
}
